/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, Image } from 'rebass';
import styled from 'styled-components';
import dayjs from 'dayjs';

import noLeavesImage from '@lib/illustrations/no-leaves-found.svg';

import {
  Typography,
  IconButton,
  DataTable,
  RadialBar,
  Previewer,
  Link,
} from '@oca/ui';
import { guidRegex, fileExtensionRegex } from '@lib/regex';
import { fileIcon, fetcher, $session } from '@features/common';
import emptyListIllustration from '@lib/illustrations/no-data-found.svg';

import { Download, Pencil } from '@oca/icons';
import { Masonry } from '@lib/masonry';
import { saveFile } from '@lib/helpers';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lib/i18n';
import { useStore } from 'effector-react';
import { LinearBar } from '@oca/ui/molecules/linear-bar';
import { Card } from '@features/dashboard/atoms';
import { CardBody } from '@features/dashboard/atoms/card';
import FileWrapper from '@oca/ui/molecules/file-wrapper';
import { ProfileBlock } from '../molecules';
import { getIqamaInfo, getBankLoanInfo } from '../lib/helpers';
import { documentLabelsMap } from '../model/static';
import { LeaveAdjustmentForm } from './leave-adjustment-form';
import {
  $leaveAdjustDialogId,
  canAdjustLeaves,
  closeLeaveAdjustDialog,
  openLeaveAdjustDialog,
} from '../model/profile';
import { formatDays } from '../lib/helpers/leave-days';

const leaveTypesLabelsMap = {
  open: <Trans>Pending</Trans>,
  approved: <Trans>Approved</Trans>,
  rejected: <Trans>Rejected</Trans>,
};

export function EmployeeProfileBlocks({ blockIndex, profile }) {
  const Block = blocks[blockIndex] || null;

  return <Box>{Block && <Block {...profile} _user={profile} />}</Box>;
}

EmployeeProfileBlocks.propTypes = {
  blockIndex: PropTypes.number.isRequired,
  profile: PropTypes.shape({
    fullName: PropTypes.string,
  }).isRequired,
};

const blocks = [
  BasicInfoBlock,
  ContactsInfoBlock,
  DocumentsInfoBlock,
  AllowancesInfoBlock,
  BankLoanInfoBlock,
];

function BasicInfoBlock({
  birthday,
  department,
  employmentStatus,
  fullName,
  gender,
  hiredDate,
  id,
  isResident,
  jobTitle,
  leaves,
  maritalStatus,
  nationality,
  rfid,
  shirtSize,
  _user,
}) {
  const iqamaInfo = getIqamaInfo(_user);
  const manager =
    _user &&
    (_user.manager ||
      _user.reportingManager ||
      (department && department.manager));
  const managerName = manager
    ? manager.fullName ||
      (manager.firstName
        ? `${manager.firstName} ${manager.lastName}`
        : manager.username) ||
      manager.name
    : null;

  const basicInfoList = [
    { label: <Trans>Full name</Trans>, value: fullName },
    { label: <Trans>Birthday</Trans>, value: midFormat(birthday) },
    { label: <Trans>Gender</Trans>, value: gender },
    { label: <Trans>Marital status</Trans>, value: maritalStatus },
    { label: <Trans>Nationality</Trans>, value: nationality },
    {
      label: <Trans>Residential status</Trans>,
      value: isResident ? <Trans>Resident</Trans> : <Trans>Non resident</Trans>,
    },
  ];

  if (iqamaInfo && iqamaInfo.expDate) {
    const iqamaLabel = isResident ? (
      <Trans>Iqama Expiry Date</Trans>
    ) : (
      <Trans>Civil ID Expiry Date</Trans>
    );
    let statusText = <Trans>Valid</Trans>;
    if (iqamaInfo.isExpired) {
      statusText = (
        <span style={{ color: '#cf1322', fontWeight: 600 }}>Expired</span>
      );
    } else if (iqamaInfo.isUpcoming) {
      statusText = (
        <span style={{ color: '#d46b08', fontWeight: 600 }}>
          Expiring in {iqamaInfo.daysRemaining} days
        </span>
      );
    }

    basicInfoList.push({
      label: iqamaLabel,
      value: (
        <span>
          {midFormat(iqamaInfo.expDate)} ({statusText})
        </span>
      ),
    });
  }

  const companyInfoList = [
    { label: <Trans>RFID</Trans>, value: rfid },
    {
      label: <Trans>Department</Trans>,
      value: department ? department.name : '',
    },
    { label: <Trans>Job title</Trans>, value: jobTitle },
    {
      label: <Trans>Employment status</Trans>,
      value:
        employmentStatus === 'full_time' ? (
          <Trans>Full time</Trans>
        ) : (
          <Trans>Part time</Trans>
        ),
    },
    { label: <Trans>Shirt size</Trans>, value: shirtSize },
    { label: <Trans>Hired date</Trans>, value: midFormat(hiredDate) },
  ];

  if (managerName) {
    companyInfoList.push({
      label: <Trans>Reporting manager</Trans>,
      value: `${managerName} ${
        manager && manager.jobTitle ? `(${manager.jobTitle})` : ''
      }`,
    });
  }

  const leavesColumns = [
    { Header: <Trans>Leave type</Trans>, accessor: 'type.name' },
    {
      Header: <Trans>From</Trans>,
      id: 'from',
      accessor: d => midFormat(d.dateFrom),
    },
    {
      Header: <Trans>To</Trans>,
      id: 'to',
      accessor: d => midFormat(d.dateTo),
    },
    {
      Header: <Trans>Status</Trans>,
      id: 'status',
      accessor: d => leaveTypesLabelsMap[d.status] || '',
    },
  ];

  const leaveAdjustmentColumns = [
    {
      Header: <Trans>Leave type</Trans>,
      id: 'leaveType',
      accessor: data => {
        const type = leaves.types.find(item => item.id === data.leaveType);
        return type ? type.name : 'N/A';
      },
    },
    {
      Header: <Trans>Created by</Trans>,
      id: 'createdByName',
      accessor: data => data.createdByName || 'N/A',
    },
    {
      Header: <Trans>Date</Trans>,
      id: 'date',
      accessor: data => fullFormat(data.date),
    },
    {
      Header: <Trans>Effective date</Trans>,
      id: 'effectiveDate',
      accessor: data => midFormat(data.effectiveDate),
    },
    {
      Header: <Trans>Amount</Trans>,
      id: 'amount',
      accessor: data => {
        const value = formatDays(Math.abs(data.adjustment));
        const sign = data.adjustment > 0 ? '+' : '-';
        return (
          <Trans>
            {sign} {value} (days)
          </Trans>
        );
      },
    },
    {
      // Entitlement after the correction, not the remaining available days.
      Header: <Trans>Entitlement after</Trans>,
      id: 'balance',
      accessor: data => formatDays(data.leaveBalanceAfter) || 'N/A',
    },
    {
      Header: <Trans>Reason</Trans>,
      accessor: 'reason',
    },
  ];

  return (
    <React.Fragment>
      <Box paddingBottom={24}>
        <ProfileBlock
          title={<Trans>Basic information</Trans>}
          list={basicInfoList}
        />
      </Box>
      <Box paddingBottom={24}>
        <ProfileBlock
          title={<Trans>Company information</Trans>}
          list={companyInfoList}
        />
      </Box>
      {leaves && (
        <ProfileBlock title={<Trans>Leaves</Trans>}>
          <LeaveTypeGrid mt={2} mb={48}>
            {leaves.types.map(type => (
              <AvailableLeaveType key={type.id} type={type} user={_user} />
            ))}
          </LeaveTypeGrid>
          <Flex flexDirection="column">
            <Box width={1}>
              <Typography marginBottom={3} variant="h2" gutterBottom>
                <Trans>Leaves history</Trans>
              </Typography>
              <DataTable
                className="-striped"
                data={leaves ? leaves.list : []}
                noDataText={
                  <Flex flexDirection="column" alignItems="center">
                    <Image src={noLeavesImage} mb={2} />
                    <Trans>No leaves</Trans>
                  </Flex>
                }
                columns={leavesColumns}
                pageSize={5}
              />
            </Box>
            {/* Only requested for viewers who may manage adjustments. */}
            {Array.isArray(leaves.adjustments) && (
              <Box width={1} marginTop={40}>
                <Typography marginBottom={3} variant="h2" gutterBottom>
                  <Trans>Leaves adjustment history</Trans>
                </Typography>
                <DataTable
                  className="-striped"
                  data={leaves.adjustments}
                  noDataText={
                    <Flex flexDirection="column" alignItems="center">
                      <Image src={noLeavesImage} mb={2} />
                      <Trans>No adjustments</Trans>
                    </Flex>
                  }
                  columns={leaveAdjustmentColumns}
                  pageSize={10}
                />
              </Box>
            )}
          </Flex>
        </ProfileBlock>
      )}
    </React.Fragment>
  );
}

function ContactsInfoBlock({
  street,
  contacts: emergencyContacts,
  email,
  homePhone,
  mobilePhone,
  workPhone,
}) {
  const contactsInfoBlock = [
    { label: <Trans>Work phone</Trans>, value: workPhone },
    { label: <Trans>Mobile phone</Trans>, value: mobilePhone },
    { label: <Trans>Home phone</Trans>, value: homePhone },
    { label: <Trans>Email</Trans>, value: email },
    { label: <Trans>Home Address</Trans>, value: street },
  ];

  return (
    <React.Fragment>
      <Box paddingBottom={24}>
        <ProfileBlock
          title={<Trans>Contact information</Trans>}
          list={contactsInfoBlock}
        />
      </Box>
      {emergencyContacts.map(
        (
          {
            relationship,
            mobilePhone: emMobilePhone,
            homePhone: emHomePhone,
            address: emAddress,
          },
          index,
        ) => {
          const list = [
            {
              label: <Trans>Name and relationship</Trans>,
              value: relationship,
            },
            { label: <Trans>Mobile phone</Trans>, value: emMobilePhone },
            { label: <Trans>Home phone</Trans>, value: emHomePhone },
            { label: <Trans>Address</Trans>, value: emAddress },
          ];

          return (
            <Box paddingBottom={24}>
              <ProfileBlock
                title={
                  emergencyContacts.length > 1 ? (
                    <Trans>Emergency contact #{index + 1}</Trans>
                  ) : (
                    <Trans>Emergency contact</Trans>
                  )
                }
                list={list}
              />
            </Box>
          );
        },
      )}
    </React.Fragment>
  );
}

function DocumentsInfoBlock({
  documents = [],
  contracts = [],
  educations = [],
  curriculumVitae = [],
  _user,
}) {
  const viewer = useStore($session);
  const isAuthorizedHR =
    viewer &&
    (viewer.topLevelPermission ||
      viewer.position === 'admin' ||
      viewer.position === 'director' ||
      (viewer.department && viewer.department.name === 'HR'));
  const isSelf = viewer && _user && viewer.id === _user.id;

  const rawDocs = documents || [];
  const permittedDocs = rawDocs.filter(doc => {
    if (isAuthorizedHR) return true;
    // Ordinary employees can only see non-restricted documents (passport, cv, certificates)
    const restricted = ['bank_loan', 'contract', 'nda', 'salary_certificate'];
    return !restricted.includes(doc.docType);
  });

  const documentItems = permittedDocs.map(
    ({ docType, expDate, files, id, uid }, index) => {
      const title = documentLabelsMap[docType] || docType;
      const docList = [];

      if (expDate) {
        const isExp = dayjs().isAfter(new Date(expDate));
        docList.push({
          label: <Trans>Expire date</Trans>,
          value: (
            <span>
              {midFormat(expDate)}{' '}
              {isExp && (
                <span style={{ color: '#cf1322', fontWeight: 600 }}>
                  (Expired)
                </span>
              )}
            </span>
          ),
        });
      }

      if (uid && !guidRegex().test(uid)) {
        docList.push({
          label: <Trans>Number</Trans>,
          value: uid,
        });
      }

      return (
        <DocumentCard
          key={id || index}
          title={title}
          list={docList}
          files={files}
        />
      );
    },
  );

  const contractItems = (isAuthorizedHR ? contracts : []).map(
    ({ docType, expDate, files, id }, index) => {
      const list = [
        {
          label: <Trans>Type</Trans>,
          value: documentLabelsMap[docType] || docType,
        },
        { label: <Trans>Expire date</Trans>, value: midFormat(expDate) },
      ];

      return (
        <DocumentCard
          key={id || index}
          title={<Trans>Signed contract {index + 1}</Trans>}
          list={list}
          files={files}
        />
      );
    },
  );

  const educationItems = educations.map(
    ({ id, institution, degree, files }, index) => {
      const list = [
        {
          label: <Trans>College/institution</Trans>,
          value: institution || 'N/A',
        },
        {
          label: <Trans>Degree / Certificate</Trans>,
          value: documentLabelsMap[degree] || degree || 'Certificate',
        },
      ];

      return (
        <DocumentCard
          key={id || index}
          title={<Trans>Education #{index + 1}</Trans>}
          list={list}
          files={files}
        />
      );
    },
  );

  const emptyEducation = (
    <EmptyDocument
      key="ed-empty"
      title={<Trans>Education</Trans>}
      description={<Trans>No educations was added yet</Trans>}
    />
  );

  const grid = [
    ...documentItems,
    ...contractItems,
    ...(educationItems.length > 0 ? educationItems : [emptyEducation]),
    curriculumVitae.length > 0 ? (
      <DocumentCard
        key="cv-card"
        title={<Trans>Curriculum vitae (CV)</Trans>}
        {...curriculumVitae[0]}
      />
    ) : (
      <EmptyDocument
        key="cv-empty"
        title={<Trans>Curriculum vitae (CV)</Trans>}
        description={<Trans>No CV was added yet</Trans>}
      />
    ),
  ];

  return <Masonry gap={24}>{grid}</Masonry>;
}

function BankLoanInfoBlock({ _user }) {
  const viewer = useStore($session);
  const isAuthorized =
    viewer &&
    (viewer.topLevelPermission ||
      viewer.position === 'admin' ||
      viewer.position === 'director' ||
      (viewer.department && viewer.department.name === 'HR'));
  const loanInfo = getBankLoanInfo(_user);

  if (!isAuthorized) {
    return (
      <Card p={4}>
        <Typography color="textSecondary">
          <Trans>
            You do not have permission to view employee bank loan details.
          </Trans>
        </Typography>
      </Card>
    );
  }

  if (!loanInfo) {
    return (
      <EmptyDocument
        title={<Trans>Bank Loan</Trans>}
        description={
          <Trans>No bank loan records or active loans for this employee.</Trans>
        }
      />
    );
  }

  const detailsList = [
    {
      label: <Trans>Bank / Financial Institution</Trans>,
      value: loanInfo.bankName,
    },
    {
      label: <Trans>Loan Status</Trans>,
      value: (
        <span
          style={{
            color: loanInfo.hasActiveLoan ? '#cf1322' : '#389e0d',
            fontWeight: 600,
            textTransform: 'uppercase',
          }}
        >
          {loanInfo.hasActiveLoan ? (
            <Trans>Active</Trans>
          ) : (
            <Trans>Expired</Trans>
          )}
        </span>
      ),
    },
    {
      label: <Trans>Loan Expiry Date</Trans>,
      value: loanInfo.expiryDate ? (
        midFormat(loanInfo.expiryDate)
      ) : (
        <Trans>Not specified</Trans>
      ),
    },
    {
      label: <Trans>Loan Reference</Trans>,
      value: loanInfo.reference || '-',
    },
  ];

  return (
    <Box>
      {loanInfo.hasActiveLoan && (
        <Box
          p={3}
          mb={3}
          bg="#fffbe6"
          style={{ border: '1px solid #ffe58f', borderRadius: 8 }}
        >
          <Typography fontWeight={600} color="#d46b08" fontSize={15} mb={1}>
            <Trans>Active Bank Loan Warning</Trans>
          </Typography>
          <Typography fontSize={13} color="#873800">
            <Trans>
              This employee has an active bank loan on file. HR review and bank
              notification are required before offboarding.
            </Trans>
          </Typography>
        </Box>
      )}

      <ProfileBlock
        title={<Trans>Bank Loan Information</Trans>}
        list={detailsList}
      >
        {loanInfo.documents && loanInfo.documents.length > 0 && (
          <Box mt={3}>
            <Typography fontSize={14} fontWeight={500} mb={2}>
              <Trans>Bank Loan Documents</Trans>
            </Typography>
            {loanInfo.documents.map((doc, idx) => (
              <DocumentCard
                key={doc.id || idx}
                title={documentLabelsMap[doc.docType] || 'Bank Loan Agreement'}
                files={doc.files}
                list={[
                  {
                    label: <Trans>Bank</Trans>,
                    value: doc.bankName || '-',
                  },
                  {
                    label: <Trans>Reference</Trans>,
                    value: doc.uid || '-',
                  },
                  {
                    label: <Trans>Expiry</Trans>,
                    value: doc.expDate ? midFormat(doc.expDate) : '-',
                  },
                ]}
              />
            ))}
          </Box>
        )}
      </ProfileBlock>
    </Box>
  );
}

function AllowancesInfoBlock({ properties }) {
  if (properties.length === 0) {
    return (
      <EmptyDocument
        title={<Trans>Allowances</Trans>}
        description={<Trans>No allowances was added yet</Trans>}
      />
    );
  }

  return (
    <Masonry breakpointCols={{ default: 2, 960: 1 }} gap={24}>
      {properties.map(
        ({ assignDate, files, id, note, returnDate, stateProperty }) => {
          const itemName =
            stateProperty && stateProperty.mark
              ? `${stateProperty.mark} ${stateProperty.model}`
              : '-';

          const itemSerialNumber =
            stateProperty && stateProperty.serialNumber
              ? stateProperty.serialNumber
              : '-';

          const list = [
            { label: <Trans>Item</Trans>, value: itemName },
            { label: <Trans>Serial #</Trans>, value: itemSerialNumber },
            {
              label: <Trans>Date assigned</Trans>,
              value: midFormat(assignDate) || <Trans>Not specified</Trans>,
            },
            {
              label: <Trans>Date returned</Trans>,
              value: midFormat(returnDate) || <Trans>Not specified</Trans>,
            },
            { label: <Trans>Note</Trans>, value: note },
          ];

          return (
            <Card padding={0}>
              <CardBody padding={0}>
                <DocumentCard
                  key={id}
                  list={list}
                  title={stateProperty.category.name}
                  files={files}
                  blockWidth={[1 / 3]}
                />
              </CardBody>
            </Card>
          );
        },
      )}
    </Masonry>
  );
}

function DocumentCard({ title, list, files = [], blockWidth = [1 / 2] }) {
  const [preview, setPreview] = React.useState(null);

  return (
    <ProfileBlock title={title} list={list} blockWidth={blockWidth}>
      {files.length > 0 && (
        <Flex marginTop={24} flexDirection="column">
          {files.map(({ fileName, file }, idx) => {
            const maybeExt = fileName.match(fileExtensionRegex());
            const ext = maybeExt ? maybeExt[1] : 'default';
            const { icon: Icon } = fileIcon.find(
              icon => icon.ext === ext || icon.ext === 'default',
            );

            return (
              <FileWrapper
                key={fileName}
                justifyContent="space-between"
                marginBottom={files.length - 1 === idx ? 0 : 12}
              >
                <File
                  alignItems="center"
                  onClick={() => setPreview({ fileName, file, fileExt: ext })}
                >
                  <Icon fontSize={24} />
                  <Typography noWrap>&nbsp;{fileName}</Typography>
                </File>
                <Box>
                  <Box
                    color="text"
                    size="small"
                    padding={8}
                    onClick={() =>
                      onDownload({ filename: fileName, url: file })
                    }
                  >
                    <Download />
                  </Box>
                </Box>
              </FileWrapper>
            );
          })}
        </Flex>
      )}
      {preview && <Previewer {...preview} onClose={() => setPreview(null)} />}
    </ProfileBlock>
  );
}

function EmptyDocument({ title, description }) {
  return (
    <ProfileBlock title={title}>
      <Flex alignItems="center">
        <Image width={122} src={emptyListIllustration} />
        <Typography>{description}</Typography>
      </Flex>
    </ProfileBlock>
  );
}

function LeaveTips() {
  return (
    <Flex justifyContent="space-around">
      <Flex alignItems="center">
        <Circle width={28} bg="primary" mr={8} />
        <Typography>
          <Trans>Days used</Trans>
        </Typography>
      </Flex>
      <Box width="2px" bg="disabled" style={{ height: 28 }} />
      <Flex alignItems="center">
        <Circle width={28} bg="#e6e6e6" mr={8} />
        <Typography>
          <Trans>Days remaining</Trans>
        </Typography>
      </Flex>
    </Flex>
  );
}

/**
 * One leave type's balance, as the server computes it. Examination and unpaid
 * types have no fixed allowance (null balance), which is not the same as zero.
 */
function AvailableLeaveType({ type, user }) {
  const viewer = useStore($session);
  const openedDialogId = useStore($leaveAdjustDialogId);
  const {
    amountUsedDays,
    leaveBalance,
    availableDays,
    annualPolicy,
    name,
  } = type;
  const isDialogOpened = openedDialogId === type.id;
  const hasAllowance = leaveBalance !== null && leaveBalance !== undefined;
  const usedDays = formatDays(amountUsedDays) || '0';
  const entitlementDays = formatDays(leaveBalance);
  const remainingDays = formatDays(availableDays);
  // Statutory (rule-based) types reject balance adjustments.
  const canAdjust = canAdjustLeaves(viewer) && !type.hrRule && hasAllowance;

  return (
    <LeaveTypeCard>
      <Flex justifyContent="space-between" alignItems="flex-start">
        <Typography fontWeight={600} fontSize={14} title={name}>
          {name}
        </Typography>
        {canAdjust && (
          <Link
            as="button"
            onClick={() => openLeaveAdjustDialog(type.id)}
            title={i18n._(t`Adjust leave`)}
          >
            <Pencil />
          </Link>
        )}
      </Flex>
      <Flex alignItems="baseline" style={{ gap: 6 }}>
        <Typography fontWeight={600} fontSize={24}>
          {usedDays}
        </Typography>
        <Typography color="textSecondary" fontSize={13}>
          {hasAllowance ? (
            <Trans>of {entitlementDays} days used</Trans>
          ) : (
            <Trans>days used</Trans>
          )}
        </Typography>
      </Flex>
      {hasAllowance && (
        <LinearBar total={leaveBalance} amount={amountUsedDays || 0} />
      )}
      <Typography color="textSecondary" fontSize={12}>
        {hasAllowance ? (
          <Trans>Available: {remainingDays} days</Trans>
        ) : (
          <Trans>No fixed allowance</Trans>
        )}
      </Typography>
      {annualPolicy && <AnnualPolicyInfo policy={annualPolicy} />}
      {isDialogOpened && (
        <LeaveAdjustmentForm
          leave={type}
          user={user}
          onDialogClose={closeLeaveAdjustDialog}
        />
      )}
    </LeaveTypeCard>
  );
}

function AnnualPolicyInfo({ policy }) {
  const period = `${midFormat(policy.periodStart)} – ${midFormat(
    policy.periodEnd,
  )}`;
  const earned = formatDays(policy.earnedDays) || '0';
  const carried = formatDays(policy.carriedDays) || '0';
  const adjusted = formatDays(policy.adjustmentDays) || '0';

  return (
    <Box pt={2} style={{ borderTop: '1px solid #EAECF0' }}>
      <Typography color="textSecondary" fontSize={12}>
        <Trans>Leave year: {period}</Trans>
      </Typography>
      <Typography color="textSecondary" fontSize={12}>
        <Trans>
          Earned {earned} · Carried over {carried} · Adjusted {adjusted}
        </Trans>
      </Typography>
    </Box>
  );
}

/* Equal-width cards that wrap, instead of flex items shrunk to their text. */
const LeaveTypeGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  grid-gap: 16px;
`;

const LeaveTypeCard = styled(Flex)`
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding: 16px 18px;
  background-color: ${({ theme }) => theme.colors.secondary};
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 12px;
`;

const Circle = styled(Box)`
  border-radius: 50%;
  height: ${({ width }) => width}px;
  box-shadow: 0px 0 0 3px inset rgba(0, 0, 0, 0.2);
`;

const File = styled(Flex)`
  overflow: hidden;
  cursor: pointer;
  :hover > p {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

function midFormat(date) {
  return date ? dayjs(new Date(date)).format('ll') : '';
}

function fullFormat(date) {
  return date ? dayjs(new Date(date)).format('lll') : '';
}

function onDownload({ url, filename }) {
  fetcher
    .get(url, { responseType: 'blob' })
    .then(res => saveFile(res.data, filename));
}
